import React from 'react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ContactForm from '../components/ContactForm';
import { COMPANY_INFO } from '../data/siteData';

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Kontakt - Hærvejens Træpleje"
        description="Kontakt Hærvejens Træpleje. Telefon: 27 13 57 87, E-mail: haervejenstraepleje@gmail.com. Seggelund Hovedvej 20, 6070 Christiansfeld."
        canonical="https://xn--hrvejenstrpleje-xlbj.dk/kontakt/"
      />

      <Breadcrumbs items={[{ label: 'Kontakt' }]} />

      {/* Header */}
      <section className="bg-[#10271c] text-white py-14 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold text-[#7dd2a6] bg-[#1a3d2c] px-3.5 py-1.5 rounded-full inline-block mb-4">
            Kontakt os
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            Kontakt Hærvejens Træpleje
          </h1>
          <p className="text-base sm:text-lg text-[#cadcd1] leading-relaxed max-w-2xl mx-auto">
            Vi glæder os til at høre fra dig! Vi kan træffes alle ugens hverdage, enten på telefon eller e-mail. Du er mere end velkommen til at tage kontakt og få en helt uforpligtende snak omkring, hvad vi kan hjælpe dig med.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Direct info cards */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#dce5df] shadow-xs space-y-6">
              <h2 className="text-xl font-bold text-[#14291d]">
                Kontaktoplysninger
              </h2>

              <div className="space-y-4 text-sm text-[#384a3e]">
                {/* Address */}
                <div className="flex items-start gap-3.5">
                  <div className="w-9 h-9 rounded-xl bg-[#e8f1ec] text-[#1b4332] flex items-center justify-center text-sm shrink-0 mt-0.5">
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-bold text-[#12281d]">{COMPANY_INFO.name}</p>
                    <p>{COMPANY_INFO.address}</p>
                    <p>
                      {COMPANY_INFO.postalCode} {COMPANY_INFO.city}
                    </p>
                    <div className="pt-2">
                      <a
                        href={COMPANY_INFO.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1b4332] hover:underline"
                      >
                        <i className="fa-solid fa-diamond-turn-right text-xs" aria-hidden="true" />
                        <span>Find vej på Google Maps</span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-3.5 pt-2 border-t border-[#edf3ef]">
                  <div className="w-9 h-9 rounded-xl bg-[#e8f1ec] text-[#1b4332] flex items-center justify-center text-sm shrink-0">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs text-[#5f7466]">Telefon:</p>
                    <a
                      href={`tel:${COMPANY_INFO.phoneRaw}`}
                      className="font-bold text-[#14291d] text-base hover:text-[#2d6a4f] transition-colors whitespace-nowrap"
                    >
                      {COMPANY_INFO.phone}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center gap-3.5 pt-2 border-t border-[#edf3ef]">
                  <div className="w-9 h-9 rounded-xl bg-[#e8f1ec] text-[#1b4332] flex items-center justify-center text-sm shrink-0">
                    <i className="fa-solid fa-envelope" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs text-[#5f7466]">E-mail:</p>
                    <a
                      href={`mailto:${COMPANY_INFO.email}`}
                      className="font-semibold text-[#14291d] hover:text-[#2d6a4f] transition-colors whitespace-nowrap"
                    >
                      {COMPANY_INFO.email}
                    </a>
                  </div>
                </div>

                {/* CVR */}
                <div className="flex items-center gap-3.5 pt-2 border-t border-[#edf3ef]">
                  <div className="w-9 h-9 rounded-xl bg-[#e8f1ec] text-[#1b4332] flex items-center justify-center text-sm shrink-0">
                    <i className="fa-solid fa-id-card" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs text-[#5f7466]">Virksomhed:</p>
                    <p className="font-semibold text-[#14291d]">CVR nr.: {COMPANY_INFO.cvr}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Maps Embed container */}
            <div className="rounded-2xl overflow-hidden border border-[#dce5df] bg-white shadow-xs">
              <iframe
                title="Hærvejens Træpleje placering på Google Maps"
                src="https://maps.google.com/maps?q=Seggelund%20Hovedvej%2020,%206070%20Christiansfeld&t=&z=13&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="280"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="p-3 bg-[#f8fbf9] text-center">
                <a
                  href={COMPANY_INFO.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-bold text-[#1b4332] hover:underline inline-flex items-center gap-1.5"
                >
                  <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                  <span>Åbn rutevejledning i Google Maps</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Column: Contact form */}
          <div className="lg:col-span-7">
            <ContactForm
              title="Få et uforpligtende tilbud"
              subtitle="Udfyld formularen herunder og vi vender tilbage med et uforpligtende tilbud inden for 24 timer."
            />
          </div>
        </div>
      </section>
    </>
  );
}
